import { useSuspenseQuery } from "@tanstack/react-query";

import { ChartLegend } from "@/components/Charts/ChartLegend";
import { MeterChart } from "@/components/Charts/MeterChart";
import { dataColors } from "@/components/Charts/theme";
import { NoData } from "@/components/NoData/NoData";
import { Stat } from "@/components/Stat/Stat";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { useSlug } from "@/hooks/useSlug";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

export function ReachAdoptionCard(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();

  const analyticsQueries = useAnalyticsQueries();
  const { data } = useSuspenseQuery(analyticsQueries.adoption());

  const missingAddresses = data.totalAddresses - data.totalAddressesInvited;
  const isDataAvailable = data.totalAddressesInvited > 0 && data.totalAddresses > 0;
  const isActionRequired = isDataAvailable && missingAddresses > 0;

  return (
    <AnalyticsCard.Root>
      <AnalyticsCard.Header
        title={t("component.analytics-widget.adoption.title")}
        tooltip={t("page.analytics-overview.chart.adoption.title-tooltip")}
      >
        <Button variant="ghostPrimary" type="link" href={routes.analytics.adoptionDetails({ slug })}>
          {t("common.action.see-more")}
        </Button>
      </AnalyticsCard.Header>
      {!data ? (
        <NoData />
      ) : (
        <div className="grid grid-cols-1 items-center justify-between gap-space-xl @lg:grid-cols-2">
          <Stack gap="md" align="center">
            <MeterChart
              primaryValue={data.reachRatePercentage}
              secondaryValue={data.adoptionRatePercentage}
              ariaLabel="Reach & adoption rate chart"
            />
            <ChartLegend
              items={[
                {
                  label: (
                    <Trans
                      i18nKey="component.analytics-widget.reach-adoption.chart.legend.primary"
                      components={{
                        b: <strong />,
                      }}
                      values={{
                        reachCount: data.invitedAddressesNotActivated + data.invitedAddressesActivated,
                        count: data.totalAddresses,
                      }}
                    />
                  ),
                  color: dataColors.success,
                },
                {
                  label: (
                    <Trans
                      i18nKey="component.analytics-widget.reach-adoption.chart.legend.secondary"
                      components={{
                        b: <strong />,
                      }}
                      values={{
                        count: data.invitedAddressesActivated,
                      }}
                    />
                  ),
                  color: dataColors.primary,
                },
              ]}
            />
          </Stack>
          <Inline gap="md">
            <section className="grid min-w-0 flex-1 grid-cols-2 gap-space-md">
              <Stat
                label={t("page.analytics-overview.chart.adoption.item.addresses-total")}
                value={data.totalAddresses}
              />
              <Stat
                label={t("page.analytics-overview.chart.adoption.item.unknown")}
                value={data.unknownStatusAddresses}
              />
            </section>
            <section className="grid min-w-0 flex-1 grid-cols-2 gap-space-md">
              <Stat
                label={t("page.analytics-overview.chart.adoption.item.addresses-invited")}
                value={data.totalAddressesInvited}
              />
              <Stat label={t("page.analytics-overview.chart.adoption.item.users-total")} value={data.totalResidents} />
            </section>

            {isActionRequired && (
              <Inline gap="lg" align="center" wrap={false}>
                <section className="text-left">
                  <h4 className="text-heading-4">
                    {t("component.analytics-widget.reach-adoption.action-title", {
                      count: missingAddresses,
                    })}
                  </h4>
                  <p className="text-text-secondary">
                    {t("component.analytics-widget.reach-adoption.action-description", {
                      count: missingAddresses,
                    })}
                  </p>
                </section>
                <Button type="link" href={routes.addresses.list({ slug })}>
                  {t("component.analytics-widget.reach-adoption.action-btn")}
                </Button>
              </Inline>
            )}
          </Inline>
        </div>
      )}
    </AnalyticsCard.Root>
  );
}

export function ReachAdoptionCompanyCard(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const { data } = useSuspenseQuery(analyticsQueries.companyAdoption());

  const missedCompanies = data.totalCompanies - data.totalCompaniesWithUsers;
  const isDataAvailable = data.totalCompaniesWithUsers > 0 || data.totalCompaniesWithOnboardedUsers > 0;
  const isActionRequired = isDataAvailable && missedCompanies > 0;

  return (
    <AnalyticsCard.Root>
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.company-adoption.title")}
        tooltip={t("page.analytics-overview.chart.company-adoption.title-tooltip")}
      >
        <Button size="sm" variant="ghostPrimary" type="link" href={routes.analytics.adoptionDetails({ slug })}>
          {t("common.action.see-more")}
        </Button>
      </AnalyticsCard.Header>
      {!data ? (
        <NoData />
      ) : (
        <div className="grid grid-cols-1 items-center justify-between gap-space-xl @md:grid-cols-2">
          <div className="flex flex-col items-center gap-space-md px-space-lg">
            <MeterChart
              primaryValue={data.reachRatePercentage}
              secondaryValue={data.adoptionRatePercentage}
              ariaLabel="Reach & adoption rate chart"
            />
            <ChartLegend
              items={[
                {
                  label: (
                    <Trans
                      i18nKey="component.analytics-widget.reach-adoption.company.chart.legend.primary"
                      components={{
                        b: <strong />,
                      }}
                      values={{
                        reachCount: data.totalCompaniesWithUsers,
                        count: data.totalCompanies,
                      }}
                    />
                  ),
                  color: dataColors.success,
                },
                {
                  label: (
                    <Trans
                      i18nKey="component.analytics-widget.reach-adoption.company.chart.legend.secondary"
                      components={{
                        b: <strong />,
                      }}
                      values={{
                        count: data.totalCompaniesWithOnboardedUsers,
                      }}
                    />
                  ),
                  color: dataColors.primary,
                },
              ]}
            />
          </div>
          <section className="flex w-full grow flex-col gap-space-sm">
            <Tiles columns={2} gap="sm">
              <Stat
                label={t("page.analytics-overview.chart.company-adoption.item.companies-total")}
                value={data.totalCompanies}
              />
              <Stat
                label={t("page.analytics-overview.chart.company-adoption.item.unknown")}
                value={data.totalCompanies - data.totalCompaniesWithUsers}
              />
              <Stat
                label={t("page.analytics-overview.chart.company-adoption.item.users-total")}
                value={data.totalResidents}
              />
            </Tiles>
            {isActionRequired && (
              <Inline gap="lg" align="center" wrap={false}>
                <section className="text-left">
                  <h4 className="text-heading-4">
                    {t("component.analytics-widget.reach-adoption.company.action-title", {
                      count: missedCompanies,
                    })}
                  </h4>
                  <p>
                    {t("component.analytics-widget.reach-adoption.company.action-description", {
                      count: missedCompanies,
                    })}
                  </p>
                </section>
                <Button type="link" href={routes.companies.list({ slug })}>
                  {t("component.analytics-widget.reach-adoption.company.action-btn")}
                </Button>
              </Inline>
            )}
          </section>
        </div>
      )}
    </AnalyticsCard.Root>
  );
}
